'use client'

import React from 'react'
import { Check, Copy } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import ChartOverlayToggles from '@/components/tv-chart/overlay-toggles'
import { OVERLAY_HINTS } from '@/components/tv-chart'
import { ambiguousNames } from '../lib/amm'
import { readHistory } from '../lib/history'
import { formatUsdPrice, usdCompact } from '../lib/format'
import useTwetchFeed from '../lib/use-twetch-feed'
import { useAlkanes } from '../lib/store'
import { mergeHistory } from './activity-list'
import AssetAdminMenu from './asset-admin-menu'
import AssetBadges, { nameFlags } from './asset-badges'
import { BadgedIcon, Change } from './asset-row'
import MarketChart from './tv-market-chart'
import { StalePricesChip } from './pools-status'
import AssetTables from './asset-tables'

/// One labeled cell of the stats strip. `null` renders a dash — none of these
/// figures is ever a fabricated zero.
function Cell({
	label,
	children,
	tinted
}: {
	label: string
	children: React.ReactNode
	tinted?: 'up' | 'down' | null
}) {
	return (
		<div
			className={cn(
				'flex shrink-0 flex-col gap-0.5 rounded-xl px-2.5 py-2',
				tinted === 'up' && 'bg-success/10',
				tinted === 'down' && 'bg-destructive/10',
				!tinted && 'bg-secondary/40'
			)}
		>
			<span
				data-testid="alkanes-stat-label"
				className="whitespace-nowrap text-xxs text-muted-foreground"
			>
				{label}
			</span>
			<span className="text-sm font-semibold tabular-nums">{children}</span>
		</div>
	)
}

/// The viewer's own trades in this asset — broadcast records merged with the
/// wallet's chain-decoded history, reduced to timestamp + side. Real trades
/// only; this is what the "My swaps" overlay plots.
function useMyTrades(assetId: string | null): { ts: number; side: 'buy' | 'sell' }[] {
	const events = useAlkanes((s) => s.activity)
	const pools = useAlkanes((s) => s.pools)
	const address = useAlkanes((s) => s.address)
	const [local, setLocal] = React.useState(() => readHistory(address))
	React.useEffect(() => {
		const load = () => setLocal(readHistory(address))
		load()
		window.addEventListener('alkanes-history-changed', load)
		return () => window.removeEventListener('alkanes-history-changed', load)
	}, [address])
	return React.useMemo(() => {
		if (!assetId) {
			return []
		}
		return mergeHistory(local, events, pools)
			.filter((r) => r.ts > 0 && (r.buyId === assetId || r.sellId === assetId))
			.map((r) => ({
				ts: Math.floor(r.ts / 1000),
				side: r.buyId === assetId ? ('buy' as const) : ('sell' as const)
			}))
	}, [assetId, local, events, pools])
}

/// The workspace's center column: identity, the stats strip, the chart with
/// its overlay row, and the Holders/Swaps/Thesis strip underneath — fomo
/// web's token page, drawn from this chain's real data. Holders and the swap
/// tape are live tables; theses wait on the Twetch signing work and say so.
export default function AssetCenter() {
	const asset = useAlkanes((s) => s.asset)
	const assets = useAlkanes((s) => s.assets)
	const verifiedIds = useAlkanes((s) => s.verifiedIds)
	const health = useAlkanes((s) => s.health)
	const stats = useAlkanes((s) => s.assetStats)
	const loggedIn = useAlkanes((s) => s.loggedIn)

	const [copied, setCopied] = React.useState(false)
	const [overlayTrades, setOverlayTrades] = React.useState(true)
	const [overlayTheses, setOverlayTheses] = React.useState(true)
	const myAddress = useAlkanes((s) => s.address)
	const thesisFeed = useTwetchFeed(Boolean(asset?.id), asset?.id ?? null)

	const ambiguous = React.useMemo(
		() => ambiguousNames(assets.map((a) => ({ id: a.id, name: a.name }))),
		[assets]
	)
	const additionalVerified = React.useMemo(
		() => assets.filter((candidate) => verifiedIds.includes(candidate.id)),
		[assets, verifiedIds]
	)
	const flags = asset ? nameFlags(asset, ambiguous, additionalVerified) : null
	const myTrades = useMyTrades(asset?.id ?? null)
	const theses = React.useMemo(
		() =>
			thesisFeed.posts.map((post) => ({
				post,
				user: thesisFeed.users[String(post.userId)]
			})),
		[thesisFeed.posts, thesisFeed.users]
	)

	const copyId = async () => {
		if (!asset) {
			return
		}
		try {
			await navigator.clipboard.writeText(asset.id)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		} catch {
			// Clipboard blocked — the id is on screen either way.
		}
	}

	const change24 = stats?.change_24h_pct ?? asset?.change_24h_pct ?? null
	const mc = stats?.market_cap_usd ?? asset?.market_cap_usd ?? null

	return (
		<section
			aria-label="Asset detail"
			data-testid="alkanes-asset-detail"
			className="flex h-full min-h-0 flex-col gap-3 overflow-hidden pr-1"
		>
			<div className="shrink-0 rounded-2xl bg-card">
				<div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pb-2 pt-3">
					{asset ? (
						<React.Fragment>
							<div className="flex min-w-0 items-center gap-2.5">
								<BadgedIcon
									token={{ id: asset.id, name: asset.name, icon: asset.icon }}
									verified={flags?.verified ?? false}
									size={44}
								/>
								<div className="min-w-0">
									<h2 className="flex items-center gap-1.5 text-lg font-semibold leading-tight">
										<span className="max-w-[260px] truncate">{asset.name || asset.id}</span>
										{flags && !flags.verified && (
											<AssetBadges flags={flags} name={asset.name} size="md" />
										)}
									</h2>
									{flags?.impostorOf ? (
										<span className="block text-xxs tabular-nums text-destructive-ink">
											Not {asset.name} ({flags.impostorOf}) — unverified {asset.id}
										</span>
									) : (
										<span className="flex items-center gap-1 text-xxs tabular-nums text-muted-foreground">
											{asset.id}
											<button
												type="button"
												onClick={copyId}
												aria-label={copied ? 'Copied' : 'Copy asset id'}
												data-testid="alkanes-copy-id"
												className="grid size-6 place-items-center rounded transition-colors hover:text-foreground"
											>
												{copied ? (
													<Check className="size-3 text-success-ink" aria-hidden="true" />
												) : (
													<Copy className="size-3" aria-hidden="true" />
												)}
											</button>
										</span>
									)}
								</div>
								<AssetAdminMenu assetId={asset.id} />
							</div>
							{health === 'stale' && <StalePricesChip />}
							<div className="ml-auto flex w-full max-w-full flex-nowrap items-center gap-2 overflow-x-auto pb-1 2xl:w-auto 2xl:pb-0">
								<Cell label="Market cap">{mc !== null ? usdCompact(mc) : '—'}</Cell>
								<Cell label="Price">
									<span data-testid="alkanes-detail-price">
										{asset.price_usd !== null ? formatUsdPrice(asset.price_usd) : '—'}
									</span>
								</Cell>
								<Cell
									label="24H change"
									tinted={change24 === null ? null : change24 >= 0 ? 'up' : 'down'}
								>
									<Change v={change24} />
								</Cell>
								<Cell label="24H Vol.">
									{usdCompact(stats?.volume_24h_usd ?? asset.volume_24h_usd)}
								</Cell>
								<Cell label="Liquidity">
									{usdCompact(stats?.liquidity_usd ?? asset.liquidity_usd)}
								</Cell>
								<Cell label="Holders">
									{stats?.holders !== null && stats?.holders !== undefined
										? stats.holders.toLocaleString('en-US')
										: '—'}
								</Cell>
							</div>
							{asset.price_usd !== null && asset.price_source !== 'pizza' && (
								<span
									data-testid="alkanes-price-source"
									className="w-full text-xxs leading-relaxed text-muted-foreground"
								>
									{asset.price_source === 'derived-2hop'
										? 'Price derived through a counterparty token — this asset has no direct frBTC pool.'
										: 'Price derived from this asset’s own pool reserves, not from the all-pools index.'}
								</span>
							)}
						</React.Fragment>
					) : health === 'down' ? (
						<h2 className="text-lg font-semibold text-muted-foreground">Markets unavailable</h2>
					) : (
						<React.Fragment>
							<Skeleton className="size-11 shrink-0 rounded-full" />
							<div className="space-y-1.5">
								<Skeleton className="h-[22px] w-32" />
								<Skeleton className="h-3 w-16" />
							</div>
							<Skeleton className="ml-auto h-12 w-72" />
						</React.Fragment>
					)}
				</div>

				<div className="h-[clamp(260px,40vh,420px)] border-t border-border/60">
					<MarketChart
						myTrades={overlayTrades ? myTrades : []}
						theses={overlayTheses ? theses : []}
					/>
				</div>

				{/* Chart overlays: My swaps is real (the wallet's own trades in
				    this asset, snapped to their bars); theses are the feed's
				    posts. Same strip as every other chart on the site. */}
				<ChartOverlayToggles
					items={[
						{
							key: 'swaps',
							label: 'My swaps',
							hint: 'Your swaps in this asset.',
							checked: overlayTrades,
							onChange: setOverlayTrades,
							count: loggedIn && overlayTrades ? myTrades.length : undefined,
							testId: 'alkanes-overlay-swaps'
						},
						{
							key: 'theses',
							label: 'Thesis',
							hint: OVERLAY_HINTS.theses,
							checked: overlayTheses,
							onChange: setOverlayTheses,
							count: theses.length,
							testId: 'alkanes-overlay-thesis'
						}
					]}
				/>
			</div>

			{/* Holders | Swaps | Thesis | My history — fomo's bottom strip,
			    shared with the phone (asset-tables.tsx). */}
			<AssetTables
				asset={asset}
				myAddress={myAddress}
				thesisFeed={thesisFeed}
				holdersCount={stats?.holders ?? null}
				className="min-h-0 flex-1"
			/>
		</section>
	)
}
